import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { useConsumerStatusSummaryQuery } from 'apollo/queries/autogenerate/hooks';
import {
  installmentPlanDataFragment,
  paymentDataFragment,
} from 'apollo/queries/autogenerate/operations';
import { chargeDataFragment } from 'apollo/queries/autogenerate/operations';
import { Charge, KYCStatus } from 'apollo/queries/autogenerate/schemas';
import { Tier, ChargeKind } from 'apollo/queries/autogenerate/schemas';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import { ACTION, SCREEN } from 'shared/modules/usage/constants/mixpanel';
import {
  isNPayConvertible,
  UnavailableReasonCode,
} from 'shared/modules/usage/utils/utils';
import Mixpanel from 'shared/vendor/Mixpanel';
import { isApplePayment } from 'utils/charge-utils';
import { isPlusUser } from 'utils/user-types';
import Receipt from 'web/modules/usage/components/organisms/Receipt';

export function getReceiptScreenType(
  isKYCed: boolean,
  charge?: chargeDataFragment
) {
  if (!charge) {
    return 'Uncaptured';
  }

  if (charge.tier === Tier.PLUS) {
    return 'Plus';
  }

  if (isNPayConvertible(charge)) {
    return isKYCed ? 'Convertible' : 'Capture';
  }

  if (
    charge.installmentPlanOptions?.unavailable &&
    charge.installmentPlanOptions.unavailable.length > 0
  ) {
    const reason = charge.installmentPlanOptions.unavailable[0].reason;

    switch (reason.code) {
      case UnavailableReasonCode.AMOUNT_TOO_LOW:
        return 'Ineligible';
      case UnavailableReasonCode.PAYMENT_BELOW_THRESHOLD:
        return 'Ineligible';
      case UnavailableReasonCode.ALREADY_CONVERTED:
        return 'Converted';
      case UnavailableReasonCode.INSTALLMENT_CHARGE:
        return 'Installment';
      case UnavailableReasonCode.INSTALLMENT_PLAN_CHARGE:
        return 'Refund';
      case UnavailableReasonCode.MERCHANT_DISABLED:
        return 'Merchant Disabled';
      default:
        return '';
    }
  }

  return getScreenType(charge);
}

function getScreenType(charge: chargeDataFragment) {
  switch (charge.kind) {
    case ChargeKind.LATE_FEE:
      return 'Late Fee';
    case ChargeKind.SETTLEMENT:
      return 'Settlement';
    case ChargeKind.REFUND:
      return 'Refund';
    case ChargeKind.CASHBACK:
      return 'Cashback';
    case ChargeKind.MULTI_PAY_CREDIT:
      return 'Multi Pay Credit';
    case ChargeKind.MULTI_PAY_DEBIT:
      return 'Multi Pay Debit';
    case ChargeKind.MANUAL_CREDIT:
      return 'Manual Credit';
    case ChargeKind.MANUAL_DEBIT:
      return 'Manual Debit';
    case ChargeKind.CAPTURE:
      return 'Capture';
  }
}

type Props = {
  charge?: Charge;
  payment?: paymentDataFragment | null;
  installmentPlan?: installmentPlanDataFragment | null;
  onCloseButtonPress: () => void;
  open: boolean;
};

export default function ReceiptDialog(props: Props) {
  const { data: consumerStatusData } = useConsumerStatusSummaryQuery({
    fetchPolicy: 'cache-only',
  });

  if (!(props.charge || props.payment)) {
    return null;
  }

  const isKYCed =
    consumerStatusData?.KYCApplicationStatus.status === KYCStatus.APPROVED;

  const isConvertNpayEligible =
    isPlusUser(
      consumerStatusData?.KYCApplicationStatus.status,
      consumerStatusData?.houkatsuStatus.status
    ) &&
    !!props.charge &&
    !isApplePayment(props.charge);

  return (
    <Dialog
      title="ご利用明細"
      onCloseButtonPress={props.onCloseButtonPress}
      open={props.open}
      style={styles.container}
      onOpen={() =>
        Mixpanel.timeEvent(`${SCREEN.RECEIPT} - ${ACTION.PAGE_VIEW}`)
      }
      onClose={() => {
        Mixpanel.trackEvent(`${SCREEN.RECEIPT} - ${ACTION.PAGE_VIEW}`, {
          'Screen Type': getReceiptScreenType(isKYCed, props.charge),
        });
      }}
      id="receipt-dialog"
    >
      <View style={styles.body}>
        <Receipt
          charge={props.charge}
          payment={props.payment}
          installmentPlan={props.installmentPlan}
          isConvertNpayEligible={isConvertNpayEligible}
        />
      </View>
    </Dialog>
  );
}

export function useReceiptDialog() {
  const [charge, setCharge] = useState<Charge>();
  const [payment, setPayment] = useState<paymentDataFragment | null>();
  const [isOpen, setIsOpen] = useState(false);

  const open = (_charge: Charge) => {
    setCharge(_charge);
    setPayment(_charge.payment);
    setIsOpen(true);
  };

  const openWithPayment = (_payment: paymentDataFragment) => {
    // To reset the value of the Charge data. If not, the Charge data will be kept and carried over to the uncaptured payments.
    setCharge(undefined);
    setPayment(_payment);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return {
    charge,
    payment,
    isOpen,
    open,
    openWithPayment,
    close,
  };
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 424,
    paddingBottom: 0,
  },
  body: {
    marginHorizontal: -24,
    backgroundColor: '#f6f8fa',
  },
});
